import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { initGA } from './lib/analytics';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useReactivationGuard } from './hooks/useReactivationGuard';
import Header from './components/Layout/Header';

const Login               = lazy(() => import('./pages/Login'));
const ForgotPassword      = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword       = lazy(() => import('./pages/ResetPassword'));
const Activate            = lazy(() => import('./pages/Activate'));
const Support             = lazy(() => import('./pages/Support'));
const Dashboard           = lazy(() => import('./pages/Dashboard'));
const Announcements       = lazy(() => import('./pages/Announcements'));
const ViewAnnouncement    = lazy(() => import('./pages/ViewAnnouncement'));
const CreateAnnouncement  = lazy(() => import('./pages/CreateAnnouncement'));
const EditAnnouncement    = lazy(() => import('./pages/EditAnnouncement'));
const Addresses           = lazy(() => import('./pages/Addresses'));
const Documents           = lazy(() => import('./pages/Documents'));
const Requests            = lazy(() => import('./pages/Requests'));
const CreateRequest       = lazy(() => import('./pages/CreateRequest'));
const ViewRequest         = lazy(() => import('./pages/ViewRequest'));
const RequestMessages     = lazy(() => import('./pages/RequestMessages'));
const Users               = lazy(() => import('./pages/Users'));
const Violations          = lazy(() => import('./pages/Violations'));
const CreateViolation     = lazy(() => import('./pages/CreateViolation'));
const ViewViolation       = lazy(() => import('./pages/ViewViolation'));
const ViolationMessages   = lazy(() => import('./pages/ViolationMessages'));
const Dues                = lazy(() => import('./pages/Dues'));
const CreateDue           = lazy(() => import('./pages/CreateDue'));
const ViewDue             = lazy(() => import('./pages/ViewDue'));
const ViewInvoice         = lazy(() => import('./pages/ViewInvoice'));
const StripeSetup         = lazy(() => import('./pages/StripeSetup'));
const StripeCallback      = lazy(() => import('./pages/StripeCallback'));
const Profile             = lazy(() => import('./pages/Profile'));
const Reactivation        = lazy(() => import('./pages/Reactivation'));
const SignupPlanSelection = lazy(() => import('./pages/signup/PlanSelection'));
const HoaSetup            = lazy(() => import('./pages/signup/HoaSetup'));
const SignupComplete       = lazy(() => import('./pages/signup/SignupComplete'));
const PlanSelection       = lazy(() => import('./pages/PlanSelection'));

// Loading component
const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-secondary-50">
    <div className="text-center">
      <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-4">
        <span className="text-white font-bold text-xl">HB</span>
      </div>
      <p className="text-secondary-600">Loading...</p>
    </div>
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role hierarchy - admin can access board routes
  if (requiredRole && user?.role !== 'admin' && user?.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main Layout component
const Layout = ({ children }) => {
  const { checking } = useReactivationGuard();

  if (checking) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header />
      <main>{children}</main>
    </div>
  );
};

// App Routes component
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
          }
        />

        <Route
          path="/forgot-password"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />
          }
        />

        <Route
          path="/reset-password"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPassword />
          }
        />

        <Route
          path="/support"
          element={<Support />}
        />

        <Route
          path="/invite/accept"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Activate />
          }
        />

        {/* Reactivation Route - Special case for canceled admins */}
        <Route
          path="/reactivation"
          element={<Reactivation />}
        />

        {/* Signup Routes */}
        <Route
          path="/signup"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignupPlanSelection />
          }
        />
        <Route
          path="/signup/hoa-setup"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <HoaSetup />
          }
        />
        <Route
          path="/signup/complete"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignupComplete />
          }
        />

        {/* Protected Routes - All Authenticated Users */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/announcements"
          element={
            <ProtectedRoute>
              <Layout>
                <Announcements />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/announcements/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <ViewAnnouncement />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/announcements/:id/edit"
          element={
            <ProtectedRoute requiredRole="board">
              <Layout>
                <EditAnnouncement />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/announcements/create"
          element={
            <ProtectedRoute requiredRole="board">
              <Layout>
                <CreateAnnouncement />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/requests"
          element={
            <ProtectedRoute>
              <Layout>
                <Requests />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/requests/create"
          element={
            <ProtectedRoute>
              <Layout>
                <CreateRequest />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/requests/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <ViewRequest />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/requests/:id/messages"
          element={
            <ProtectedRoute>
              <Layout>
                <RequestMessages />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <Layout>
                <Documents />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dues"
          element={
            <ProtectedRoute>
              <Layout>
                <Dues />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dues/create"
          element={
            <ProtectedRoute requiredRole="board">
              <Layout>
                <CreateDue />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dues/stripe-callback"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <StripeCallback />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dues/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <ViewDue />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <ViewInvoice />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/stripe-setup"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <StripeSetup />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Profile Route */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Plan Selection Route - Admin Only */}
        <Route
          path="/plan-selection"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <PlanSelection />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Board & Admin Routes */}
        <Route
          path="/users"
          element={
            <ProtectedRoute requiredRole="board">
              <Layout>
                <Users />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/addresses"
          element={
            <ProtectedRoute requiredRole="board">
              <Layout>
                <Addresses />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/compliance"
          element={
            <ProtectedRoute>
              <Layout>
                <Violations />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/compliance/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <ViewViolation />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/compliance/:id/messages"
          element={
            <ProtectedRoute>
              <Layout>
                <ViolationMessages />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/compliance/create"
          element={
            <ProtectedRoute requiredRole="board">
              <Layout>
                <CreateViolation />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* 404 Page */}
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center bg-secondary-50">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-secondary-900 mb-4">404</h1>
                <p className="text-secondary-600 mb-4">Page not found</p>
                <a href="/dashboard" className="btn btn-primary">
                  Go to Dashboard
                </a>
              </div>
            </div>
          }
        />
      </Routes>
    </Suspense>
  );
};

// Main App component
function App() {
  useEffect(() => { initGA(); }, []);

  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
      <Toaster position="bottom-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
